import * as React from 'react';

import { useReactFlow } from '@xyflow/react';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $editorNodes,
  $editorEdges,
  $setEditorNodes,
  $setEditorEdges,
  $selectedNodeIds,
  $setSelectedNodeIds,
  $clearSelection,
} from '../../../_state/automationEditor.state';
import {
  $hasClipboardContent,
  $copyNodes,
  $pasteNodes,
} from '../../../_state/flowClipboard.state';
import { isTriggerNodeType } from '../nodes/types';

export type UseFlowClipboardOptions = {
  enabled?: boolean;
};

export const useFlowClipboard = (options: UseFlowClipboardOptions = {}) => {
  const { enabled = true } = options;

  const reactFlow = useReactFlow();

  const nodes = useAtomValue($editorNodes);
  const edges = useAtomValue($editorEdges);
  const selectedIds = useAtomValue($selectedNodeIds);
  const hasClipboard = useAtomValue($hasClipboardContent);

  const setEditorNodes = useSetAtom($setEditorNodes);
  const setEditorEdges = useSetAtom($setEditorEdges);
  const setSelectedNodeIds = useSetAtom($setSelectedNodeIds);
  const clearSelection = useSetAtom($clearSelection);
  const copyNodesAtom = useSetAtom($copyNodes);
  const pasteNodesAtom = useSetAtom($pasteNodes);

  // Copy selected nodes
  const copy = React.useCallback(() => {
    if (!enabled || selectedIds.size === 0) return;

    const selectedNodes = nodes.filter((n) => selectedIds.has(n.id));
    copyNodesAtom({ selectedNodes, allEdges: edges });
  }, [enabled, nodes, edges, selectedIds, copyNodesAtom]);

  // Paste nodes at position
  const paste = React.useCallback(
    (cursorPosition?: { x: number; y: number }) => {
      if (!enabled || !hasClipboard) return;

      // Convert screen to flow coordinates if position provided
      const position = cursorPosition
        ? reactFlow.screenToFlowPosition(cursorPosition)
        : undefined;

      const result = pasteNodesAtom({ position });
      if (!result) return;

      // Add nodes and edges
      setEditorNodes((prev) => [...prev, ...result.nodes]);
      setEditorEdges((prev) => [...prev, ...result.edges]);

      // Select pasted nodes
      setSelectedNodeIds(new Set(result.nodes.map((n) => n.id)));
    },
    [
      enabled,
      hasClipboard,
      reactFlow,
      pasteNodesAtom,
      setEditorNodes,
      setEditorEdges,
      setSelectedNodeIds,
    ],
  );

  // Cut = copy + delete
  const cut = React.useCallback(() => {
    if (!enabled || selectedIds.size === 0) return;

    // Copy first
    copy();

    // Delete selected (but not triggers)
    const nodeIdsToRemove = new Set<string>();
    nodes.forEach((node) => {
      if (
        selectedIds.has(node.id) &&
        (!node.type || !isTriggerNodeType(node.type))
      ) {
        nodeIdsToRemove.add(node.id);
      }
    });

    if (nodeIdsToRemove.size === 0) return;

    setEditorNodes((prev) => prev.filter((n) => !nodeIdsToRemove.has(n.id)));
    setEditorEdges((prev) =>
      prev.filter(
        (e) => !nodeIdsToRemove.has(e.source) && !nodeIdsToRemove.has(e.target),
      ),
    );
    clearSelection();
  }, [
    enabled,
    copy,
    nodes,
    selectedIds,
    setEditorNodes,
    setEditorEdges,
    clearSelection,
  ]);

  return {
    copy,
    paste,
    cut,
    hasClipboard: enabled && hasClipboard,
    hasSelection: selectedIds.size > 0,
  };
};
